import React, { useCallback, useEffect, useReducer, useState } from 'react';

import { Box, useTheme } from '@chakra-ui/react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
  TableBody,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import { PlainTextHeader } from 'Deer/components/HeaderComponents/PlainTextHeader';
import useForceUpdate from 'components/hooks/useForceUpdate';
import { useDispatch, useSelector } from 'react-redux';
import { selectLoggedInEmployeeData, selectLoggedInEmployeeId } from 'selectors/loggedInEmployee';
import styled from 'styled-components';
import { Container, StyledTableCell } from 'styles/admin';
import { ButtonContainer } from 'styles/auth';
import { SpaceBetweenContainer, StyledGrid } from 'styles/common';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { ADMIN_ROLES_MAP_ORDER, USER_ROLES } from 'constants/ProjectConstants';
import { makeCommaSeparatedString } from 'constants/helper';
import { DotsIcon, RemoveIconV2 } from 'constants/icons';
import { sendPageViewEvent } from 'constants/mixpanelHelper';
import theme from 'constants/theme';

import { getEmployeesInfo } from 'services/admin';
import { removeEmployee } from 'services/employee';
import { showErrorSnackbar, showSuccessSnackbar } from 'services/snackbar';

import { ButtonFilled } from 'uiToolkit/Button';
import CircularOutlineIcon from 'uiToolkit/CircularOutlineIcon';
import { Typography13 } from 'uiToolkit/Typography';
import { Button as ButtonV1 } from 'uiToolkitV1/Button';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import BulkUploadPanel from './BulkUploadSidePanel';
import AddEmployeePanel from './EmployeeSidePanel/employeeSidePanel';
import { HeaderBar } from './HeaderBar';
import { SkeletonContainer, StyledEmpTable, StyledEmpTableCell, StyledEmpTableHeadCell } from './styles';

const StyledDialog = styled(Dialog)`
  & .MuiDialog-container .MuiDialog-paper {
    box-shadow: none;
    width: 390px;
    padding: 24px;
  }
`;

const StyledDialogTitle = styled(DialogTitle)`
  h2 {
    font-size: 1.35rem;
    font-weight: 600;
  }
`;

const StyledDotsIcon = styled(DotsIcon)`
  width: 13px;
  margin-top: 1px;
`;

const StyledMenu = styled(Menu)`
  ul {
    padding-top: 10px;
    padding-bottom: 10px;
  }
`;

const StyledMenuItem = styled(MenuItem)`
  padding-right: 30px;
  width: 100%;

  display: flex;
  align-items: center;
`;

const StyledTableActionGrid = styled(StyledGrid)`
  visibility: hidden;
`;

const StyledTableRow = styled(TableRow)`
  &:hover {
    background-color: ${(props) => props.theme.palette.background.lightBlue};
    ${StyledTableActionGrid} {
      visibility: visible;
    }
  }
`;

const headerList = [
  {
    id: 'firstName',
    title: 'First Name',
    width: '10%',
  },
  {
    id: 'lastName',
    title: 'Last Name',
    width: '10%',
  },
  {
    id: 'email',
    title: 'Employee Email Id',
    width: '20%',
  },
  {
    id: 'managerEmail',
    title: 'Manager Email Id',
    width: '15%',
  },
  {
    id: 'employeeFunction',
    title: 'Function',
    width: '10%',
  },
  {
    id: 'designation',
    title: 'Designation',
    width: '10%',
  },
  {
    id: 'employeeAccesses',
    title: 'Admin Access',
    width: '15%',
  },
  {
    id: 'icon',
    title: '',
    width: '10%',
  },
];

// eslint-disable-next-line no-shadow
const TableHeader = ({ headerList }) => (
  <TableHead>
    <TableRow>
      {headerList.map(({ id, title, width }) => (
        <StyledEmpTableHeadCell width={width} key={`header-${id}`}>
          {title}
        </StyledEmpTableHeadCell>
      ))}
    </TableRow>
  </TableHead>
);

const DisplayRowView = ({ data, employeeDeleted }) => {
  const dispatch = useDispatch();
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const loggedInEmployeeId = useSelector(selectLoggedInEmployeeId);

  const chakraTheme = useTheme();

  const deleteEmployeeClicked = (employee) => {
    setSelectedEmployee(employee);
    setOpenConfirmDialog(true);
  };

  const handleDialogCancel = () => {
    setOpenConfirmDialog(false);
    setSelectedEmployee(null);
  };

  const handleDialogConfirm = () => {
    removeEmployee({ employeeId: selectedEmployee.id })
      .then((res) => {
        dispatch(showSuccessSnackbar(res.message || 'Employee is successfully removed'));
        employeeDeleted(selectedEmployee.id);
      })
      .catch((err) => {
        dispatch(showErrorSnackbar(err.message));
      })
      .finally(() => {
        setOpenConfirmDialog(false);
        setSelectedEmployee(null);
      });
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const { enabledFeatures } = useSelector(selectLoggedInEmployeeData);

  const USER_ROLE_MAP = USER_ROLES({ enabledFeatures }).reduce(
    (prev, { value, label }) => ({ ...prev, [value]: label }),
    {}
  );

  const returnAdminAccessesString = (accessesArray) => {
    if (Array.isArray(accessesArray) && accessesArray.length) {
      // have to sort on FE because BE isn't sending sorted array

      const sortedAdminAccess = accessesArray
        .map((access) => ({ access, order: ADMIN_ROLES_MAP_ORDER[access]?.order }))
        .filter(({ order }) => order !== undefined)
        .sort((a, b) => a.order - b.order);
      const accessesLabelArray = sortedAdminAccess
        .map(({ access }) => USER_ROLE_MAP[access])
        .filter((access) => access);
      return makeCommaSeparatedString(accessesLabelArray);
    }
    return '-';
  };

  const handleMoreOptionsMenuOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleMoreOptionsMenuClose = (e) => {
    e.stopPropagation();
    setAnchorEl(null);
  };

  const handleOptionSelect = (e, option) => {
    handleMoreOptionsMenuClose(e);
    switch (option) {
      case 'DELETE':
        deleteEmployeeClicked(data);
        break;
      default:
        break;
    }
  };

  return (
    <>
      {headerList
        .filter(({ id }) => id !== 'icon')
        .map(({ id }) => (
          <StyledEmpTableCell key={`data-cell-${id}`}>
            {/* eslint-disable-next-line no-nested-ternary */}
            {data[id] && data[id] !== ''
              ? id === 'employeeAccesses'
                ? returnAdminAccessesString(data[id])
                : data[id]
              : '-'}
          </StyledEmpTableCell>
        ))}
      <StyledTableCell border>
        <StyledTableActionGrid container alignItems="center" justify="flex-end" wrap="nowrap">
          {loggedInEmployeeId !== data.id ? (
            <>
              <CircularOutlineIcon
                leftGutter={1}
                rightGutter={2}
                onClick={handleMoreOptionsMenuOpen}
                color="primary"
                icon={StyledDotsIcon}
              />
              <StyledMenu
                disablePortal
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 16,
                }}
                getContentAnchorEl={null}
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMoreOptionsMenuClose}
              >
                <StyledMenuItem onClick={(e) => handleOptionSelect(e, 'DELETE')}>
                  <RemoveIconV2 size="fixed12px" color={theme.palette.background.black.v1} />
                  <Typography13 leftGutter={1.5} color={theme.palette.text.blackv1}>
                    Delete
                  </Typography13>
                </StyledMenuItem>
              </StyledMenu>
            </>
          ) : null}

          <CircularOutlineIcon color="primary" />
        </StyledTableActionGrid>
      </StyledTableCell>
      <StyledDialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <StyledDialogTitle style={{ padding: 0 }} id="alert-dialog-title">
          Remove employee
        </StyledDialogTitle>
        <DialogContent style={{ padding: 0, marginTop: '20px' }}>
          <TextBodyMedium color={chakraTheme.colors.text.g1} sx={{ margin: 0, marginBottom: '8px' }}>
            Do you want to remove {selectedEmployee && selectedEmployee.displayName}?
          </TextBodyMedium>
          <TextBodyMedium color={chakraTheme.colors.text.g2} sx={{ margin: 0 }}>
            Note: If an employee has direct reports, the deletion can take upto a few minutes.
          </TextBodyMedium>
        </DialogContent>
        <DialogActions style={{ padding: 0, marginTop: '28px' }}>
          <ButtonV1 style={{ marginRight: '16px' }} onClick={handleDialogCancel} variant="outlined">
            Cancel
          </ButtonV1>
          <ButtonV1 onClick={handleDialogConfirm}>Remove</ButtonV1>
        </DialogActions>
      </StyledDialog>
    </>
  );
};

const rowsPerPageOptions = [10, 20, 30];

const initState = {
  employeesList: [],
  loading: false,
  searchText: '',
  rowsLimit: rowsPerPageOptions[0],
  currentPage: 0,
  hasMoreData: true,
};

const reducer = (state, { type, payload }) => {
  switch (type) {
    case 'FETCHING_DATA':
      return { ...state, loading: true };
    case 'FETCHING_DATA_SUCCESSFUL':
      return {
        ...state,
        loading: false,
        employeesList: payload.data.slice(0, state.rowsLimit),
        hasMoreData: payload.data.length > state.rowsLimit,
      };
    case 'FETCHING_DATA_FAILED':
      return { ...state, loading: false };
    case 'UPDATE_EMPLOYEE': {
      const { editingIndex, editedEmployee } = payload.data;
      const updatedEmployeesList = [
        ...state.employeesList.slice(0, editingIndex),
        editedEmployee,
        ...state.employeesList.slice(editingIndex + 1),
      ];

      console.log('updatedEmployeesList', updatedEmployeesList);

      return { ...state, employeesList: updatedEmployeesList };
    }
    case 'DELETE_EMPLOYEE': {
      const { employeeId } = payload.data;
      return {
        ...state,
        employeesList: state.employeesList.filter((employee) => employee.id !== employeeId),
      };
    }
    case 'SET_SEARCH_TEXT':
      return { ...state, searchText: payload.data, currentPage: 0 };
    case 'SET_ROWS_LIMIT':
      return { ...state, rowsLimit: payload.data, currentPage: 0 };
    case 'SET_CURRENT_PAGE':
      return { ...state, currentPage: payload.data };
    default:
      return state;
  }
};

const initialSidePanelState = {
  open: false,
  mode: 'add', // 'add' || 'edit'
  editEmployee: {},
  editingIndex: -1,
};

const sidePanelReducer = (state, { type, payload }) => {
  switch (type) {
    // side panel actions
    case 'ADD_EMPLOYEE':
      return { ...state, open: true, mode: 'add', editEmployee: {} };
    case 'EDIT_EMPLOYEE':
      return { open: true, mode: 'edit', editEmployee: payload.data, editingIndex: payload.index };
    case 'CLOSE_SIDE_PANEL':
      return { ...initialSidePanelState };
    default:
      return state;
  }
};

const Employees = () => {
  const dispatch = useDispatch();

  const [{ employeesList, loading, searchText, rowsLimit, currentPage, hasMoreData }, mainDispatch] = useReducer(
    reducer,
    initState
  );

  const [sidePanel, sidePanelDispatch] = useReducer(sidePanelReducer, initialSidePanelState);
  const [bulkUploadSidePanel, setBulkUploadSidePanel] = useState(false);

  const [reloadCount, forceReloadCount] = useForceUpdate();

  const getEmployeeData = () => {
    mainDispatch({ type: 'FETCHING_DATA' });

    getEmployeesInfo({ searchText, offset: currentPage * rowsLimit, limit: rowsLimit + 1 })
      .then((employees) => {
        mainDispatch({
          type: 'FETCHING_DATA_SUCCESSFUL',
          payload: { data: employees },
        });
      })
      .catch((err) => {
        dispatch(showErrorSnackbar(err.message));
        mainDispatch({ type: 'FETCHING_DATA_FAILED' });
      });
  };
  // eslint-disable-next-line no-shadow
  const submitSearch = (searchText) => {
    mainDispatch({
      type: 'SET_SEARCH_TEXT',
      payload: { data: searchText },
    });
  };

  const editEmployee = (employeeData, rowIndex) => {
    sidePanelDispatch({
      type: 'EDIT_EMPLOYEE',
      payload: { data: employeeData, index: rowIndex },
    });
  };

  const addEmployee = () => {
    sidePanelDispatch({
      type: 'ADD_EMPLOYEE',
    });
  };

  const closeSidePanel = () => {
    sidePanelDispatch({
      type: 'CLOSE_SIDE_PANEL',
    });
  };

  const employeeEdited = (editedEmployee) => {
    mainDispatch({
      type: 'UPDATE_EMPLOYEE',
      payload: {
        data: { editingIndex: sidePanel.editingIndex, editedEmployee },
      },
    });

    closeSidePanel();
  };

  const employeeAdded = () => {
    closeSidePanel();
    getEmployeeData();
    forceReloadCount();
  };

  const onChangePage = (_, newPage) => {
    mainDispatch({
      type: 'SET_CURRENT_PAGE',
      payload: { data: newPage },
    });
  };

  const onChangeRowsPerPage = ({ target }) => {
    mainDispatch({
      type: 'SET_ROWS_LIMIT',
      payload: { data: target.value },
    });
  };

  const employeeDeleted = (employeeId) => {
    mainDispatch({
      type: 'DELETE_EMPLOYEE',
      payload: {
        data: { employeeId },
      },
    });
    forceReloadCount();
  };

  useEffect(() => {
    getEmployeeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, rowsLimit, currentPage]);

  const openBulkUploadSidePanel = useCallback(() => {
    setBulkUploadSidePanel(true);
  }, [setBulkUploadSidePanel]);

  const closeBulkUploadSidePanel = useCallback(() => {
    setBulkUploadSidePanel(false);
  }, [setBulkUploadSidePanel]);

  const onSuccessBulkUpload = useCallback(() => {
    forceReloadCount();
    // closeBulkUploadSidePanel();
  }, [forceReloadCount, closeBulkUploadSidePanel]);

  useEffect(() => {
    if (checkNewNavEnabled()) {
      sendPageViewEvent({
        eventProperties: {
          page: 'Company-settings-employee details',
        },
      });
    }
  }, []);

  return (
    <>
      <Container>
        <SpaceBetweenContainer style={{ alignItems: 'center' }}>
          <PlainTextHeader>Employee details</PlainTextHeader>
          <ButtonContainer>
            <ButtonFilled width="160px" rightGutter={2} color="primary" onClick={openBulkUploadSidePanel}>
              <Typography13 style={{ textTransform: 'none' }} color="white">
                Bulk Upload
              </Typography13>
            </ButtonFilled>
            <ButtonFilled width="160px" color="primary" onClick={addEmployee}>
              <Typography13 style={{ textTransform: 'none' }} color="white">
                Add Employee
              </Typography13>
            </ButtonFilled>
          </ButtonContainer>
        </SpaceBetweenContainer>

        <HeaderBar searchText={searchText} onSearchSubmit={submitSearch} reloadCount={reloadCount} />
        <Box borderRadius="8px" boxShadow="0px 0px 2px 0px rgba(0, 0, 0, 0.24)" background="white">
          {loading ? (
            <SkeletonContainer>
              <Skeleton width="60%" />
              <Skeleton width="40%" />
              <Skeleton width="50%" />
            </SkeletonContainer>
          ) : (
            <StyledEmpTable size="small" aria-label="a dense table">
              <TableHeader headerList={headerList} />
              <TableBody>
                {employeesList.length > 0 ? (
                  employeesList.map((data, rowIndex) => (
                    // eslint-disable-next-line react/jsx-no-literals
                    // eslint-disable-next-line react/no-array-index-key
                    <React.Fragment key={`employee-${rowIndex}`}>
                      <StyledTableRow onClick={() => editEmployee(data, rowIndex)}>
                        <DisplayRowView
                          data={data}
                          // editEmployee={() => editEmployee(data, rowIndex)}
                          employeeDeleted={employeeDeleted}
                        />
                      </StyledTableRow>
                    </React.Fragment>
                  ))
                ) : (
                  <TableRow>
                    <StyledEmpTableCell align="center" colSpan={headerList.length}>
                      <Typography variant="body1">No data</Typography>
                    </StyledEmpTableCell>
                  </TableRow>
                )}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <StyledEmpTableCell padding="none" align="right" colSpan={headerList.length}>
                    <TablePagination
                      component="div"
                      rowsPerPage={rowsLimit}
                      page={currentPage}
                      labelDisplayedRows={() => ''}
                      rowsPerPageOptions={rowsPerPageOptions}
                      onChangePage={onChangePage}
                      onChangeRowsPerPage={onChangeRowsPerPage}
                      nextIconButtonProps={{ disabled: !hasMoreData }}
                    />
                  </StyledEmpTableCell>
                </TableRow>
              </TableFooter>
            </StyledEmpTable>
          )}
        </Box>
      </Container>
      <AddEmployeePanel
        open={sidePanel.open}
        onCancel={closeSidePanel}
        onSuccess={sidePanel.mode === 'add' ? employeeAdded : employeeEdited}
        data={sidePanel.editEmployee}
        mode={sidePanel.mode}
        reloadData={getEmployeeData}
      />
      <BulkUploadPanel open={bulkUploadSidePanel} onCancel={closeBulkUploadSidePanel} onSuccess={onSuccessBulkUpload} />
    </>
  );
};

export default Employees;
